import { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import {
    Table,
    Card,
    Spin,
    Button,
    Input,
    Select,
    Row,
    Col,
    message,
    Avatar,
    Modal,
    Typography,
} from "antd";
import { SearchOutlined, UserOutlined, ClearOutlined } from "@ant-design/icons";
import EditAttendance from "./EditAttendance";

const { Option } = Select;
const { Text } = Typography;

export default function ImproperAttendance() {
    const [employees, setEmployees] = useState([]);
    const { backUrl, authData } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const [showEdit, setShowEdit] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchCode, setSearchCode] = useState("");
    const [searchName, setSearchName] = useState("");

    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);

    // ── Query builder — accepts explicit overrides so stale state is never an issue ──
    const buildQueryParams = (overrides = {}, includePagination = true) => {
        const values = {
            page: currentPage,
            page_size: pageSize,
            emp_code: searchCode.trim(),
            employee_name: searchName.trim(),
            organisation: selectedOrg,
            department: selectedDept,
            designation: selectedDesig,
            ...overrides,
        };

        const params = new URLSearchParams();
        if (includePagination) {
            params.append("page", values.page);
            params.append("page_size", values.page_size);
        }
        if (values.emp_code) params.append("emp_code", values.emp_code);
        if (values.employee_name) params.append("employee_name", values.employee_name);
        if (values.organisation) params.append("organisation", values.organisation);
        if (values.department) params.append("department", values.department);
        if (values.designation) params.append("designation", values.designation);

        return params.toString();
    };

    // ── Data fetchers ──────────────────────────────────────────────────────────
    const fetchEmployees = useCallback(
        async (overrides = {}) => {
            try {
                setLoading(true);
                const qs = buildQueryParams(overrides);
                const url = `${backUrl}/api/emp/employees/${qs ? `?${qs}` : ""}`;
                const res = await axios.get(url, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
                setEmployees(res.data.results || res.data);
                setTotalCount(res.data.count ?? res.data.length ?? 0);
            } catch (error) {
                console.error("Error fetching employees:", error);
                message.error("Failed to fetch employees");
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [backUrl, authData?.access, currentPage, pageSize, searchCode, searchName, selectedOrg, selectedDept, selectedDesig]
    );

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganizations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
            message.error("Failed to fetch organizations");
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDepartments(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    // ── Effects ────────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Single effect handles initial load + page/size changes
    useEffect(() => {
        fetchEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize]);

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleFilter = () => {
        if (currentPage === 1) {
            fetchEmployees(); // page hasn't changed so effect won't fire — call manually
        } else {
            setCurrentPage(1); // triggers the effect which calls fetchEmployees
        }
    };

    const handleClearFilters = () => {
        setSearchCode("");
        setSearchName("");
        setSelectedOrg(null);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        setPageSize(50);

        // Pass cleared values directly so we don't rely on stale state
        fetchEmployees({
            emp_code: "",
            employee_name: "",
            organisation: null,
            department: null,
            designation: null,
            page: 1,
            page_size: 50,
        });
    };

    const handleOpenEdit = (record) => {
        setSelectedEmployee(record);
        setShowEdit(true);
    };

    const handleCloseEdit = () => {
        setShowEdit(false);
        setSelectedEmployee(null);
        fetchEmployees();
    };

    // ── Helpers ────────────────────────────────────────────────────────────────
    const renderValue = (value) =>
        value === null || value === undefined || value === "" ? (
            <Text type="secondary">—</Text>
        ) : (
            value
        );

    const isFiltered = searchCode || searchName || selectedOrg || selectedDept || selectedDesig;

    // ── Columns ────────────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Employee",
            dataIndex: ["user", "username"],
            key: "emp_code",
            fixed: "left",
            width: 220,
            render: (text, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar
                        size={42}
                        src={record.photo}
                        icon={!record.photo && <UserOutlined />}
                        style={{
                            backgroundColor: !record.photo ? "#0e497b" : undefined,
                            flexShrink: 0,
                        }}
                    />
                    <div style={{ lineHeight: 1.3 }}>
                        <div>
                            <a
                                onClick={() => handleOpenEdit(record)}
                                style={{ color: "#0e497b", fontWeight: 600, cursor: "pointer" }}
                            >
                                {text}
                            </a>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {[record.first_name, record.surname, record.last_name]
                                .filter(Boolean)
                                .join(" ")}
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Organisation",
            dataIndex: "organisation_name",
            key: "organisation_name",
            render: renderValue,
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department_name",
            render: renderValue,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation_name",
            render: renderValue,
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            {/* Edit Attendance Modal */}
            <Modal
                open={showEdit}
                onCancel={handleCloseEdit}
                footer={null}
                destroyOnClose
                width={800}
            >
                <EditAttendance employee={selectedEmployee} onClose={handleCloseEdit} />
            </Modal>

            {/* Filter Card */}
            <Card
                bordered
                style={{
                    marginBottom: 20,
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                }}
            >
                {/* Action Buttons */}
                <Row justify="space-between" align="middle" style={{ marginBottom: 14 }}>
                    <Col>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleFilter}
                            style={{
                                marginRight: 8,
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                            }}
                        >
                            Filter
                        </Button>
                        <Button
                            icon={<ClearOutlined />}
                            onClick={handleClearFilters}
                            disabled={!isFiltered}
                            style={{ backgroundColor: "#f5f5f5", color: "#000" }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>

                {/* Row 1: Code, Name, Org, Dept */}
                <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            allowClear
                            placeholder="Search Employee Code"
                            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                            value={searchCode}
                            onChange={(e) => setSearchCode(e.target.value)}
                            onPressEnter={handleFilter}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            allowClear
                            placeholder="Search Employee Name"
                            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={handleFilter}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Select Organization"
                            style={{ width: "100%" }}
                            value={selectedOrg}
                            onChange={(val) => {
                                setSelectedOrg(val);
                                setSelectedDept(null);
                                setSelectedDesig(null);
                                setDepartments([]);
                                setDesignations([]);
                                if (val) fetchDepartments(val);
                            }}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {organizations.map((o) => (
                                <Option key={o.id} value={o.id}>
                                    {o.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={selectedDept}
                            onChange={(val) => {
                                setSelectedDept(val);
                                setSelectedDesig(null);
                                setDesignations([]);
                                if (val) fetchDesignations(val);
                            }}
                            disabled={!selectedOrg}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map((d) => (
                                <Option key={d.id} value={d.id}>
                                    {d.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                {/* Row 2: Designation */}
                <Row gutter={[16, 12]}>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={selectedDesig}
                            onChange={(val) => setSelectedDesig(val)}
                            disabled={!selectedDept}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {designations.map((d) => (
                                <Option key={d.id} value={d.id}>
                                    {d.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                    <Spin size="large" tip="Loading employees..." />
                </div>
            ) : (
                <Table
                    rowKey="id"
                    dataSource={employees}
                    columns={columns}
                    bordered
                    size="middle"
                    locale={{ emptyText: "No improper attendance records found." }}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                        onShowSizeChange: (_, size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        },
                        showTotal: (total, range) =>
                            `${range[0]}–${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: 900 }}
                    rowClassName={(_, index) =>
                        index % 2 === 0 ? "table-row-light" : "table-row-dark"
                    }
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        fontWeight: 600,
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}
        </>
    );
}